import React, { useState, useEffect } from "react";
import { gsap } from "gsap";
import "./CreateForm.css";
import FormCreation from "./FormCreation/FormCreation";
import ModifyForm from "./ModifyForm/ModifyForm";
import { useAuth } from "../../../Security/AuthProvider";
import { useNotification } from '../../../Utils/Commons/Notification/NotificationContext';

const DeleteConfirmationPopup = ({ onConfirm, onCancel, isDeleting }) => {
  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isDeleting) {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel, isDeleting]);

  return (
    <div className="delete-confirmation-popup">
      <p>Êtes-vous sûr de vouloir supprimer ce formulaire ?</p>
      <div className="delete-confirmation-buttons">
        <button 
          onClick={onConfirm} 
          disabled={isDeleting}
          style={{ opacity: isDeleting ? 0.6 : 1 }}
        >
          {isDeleting ? "Suppression..." : "Oui"}
        </button>
        <button 
          onClick={onCancel} 
          disabled={isDeleting}
          style={{ opacity: isDeleting ? 0.6 : 1 }}
        >
          Non
        </button>
      </div>
    </div>
  );
};

function CreateForm() {
  const [showPopup, setShowPopup] = useState(false);
  const [showModifyPopup, setShowModifyPopup] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [forms, setForms] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowPopup(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const cards = document.querySelectorAll(".info-card");
    cards.forEach((card) => {
      card.addEventListener("mouseenter", () => {
        gsap.to(card, {
          "--shadow-spread": "6px",
          duration: 0.15,
          ease: "power1.inOut",
        });
      });
      card.addEventListener("mouseleave", () => {
        gsap.to(card, {
          "--shadow-spread": "4px",
          duration: 0.15,
          ease: "power1.inOut",
        });
      });
    });

    return () => {
      cards.forEach((card) => {
        card.removeEventListener("mouseenter", () => {});
        card.removeEventListener("mouseleave", () => {});
      });
    };
  }, [forms]);

  const deleteForm = async (id) => {
    setIsDeleting(true); // Commencer le chargement
    try {
      console.log("Tentative de suppression du formulaire:", id);
      
      const response = await fetch("/forms/delete", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Erreur lors de la suppression:", errorData);
        
        // Afficher un message d'erreur plus spécifique
        if (errorData.error?.includes("utilisé par des opérations")) {
          notifyError("❌ Impossible de supprimer ce formulaire car il est utilisé par des opérations. Veuillez d'abord dissocier le formulaire des opérations.");
        } else if (errorData.error?.includes("introuvable")) {
          notifyError("❌ Ce formulaire n'existe plus ou a déjà été supprimé.");
        } else {
          notifyError(`❌ Erreur lors de la suppression: ${errorData.error || "Erreur inconnue"}`);
        }
        return;
      }

      const result = await response.json();
      console.log("Formulaire supprimé avec succès:", result);
      
      // 1. Mise à jour immédiate de l'état local (pour une réaction instantanée)
      setForms(prevForms => prevForms.filter(form => form.id !== id));
      
      // 2. Fermer les modales
      setShowDeleteConfirmation(false);
      setSelectedForm(null);
      
      // 3. Refetch complet pour s'assurer de la cohérence
      await fetchForms(true); // Force refresh
      
      // 4. Afficher un message de succès avec information sur les RDV détachés
      if (result.detachedRdv && result.detachedRdv > 0) {
        notifySuccess(`✅ Formulaire supprimé avec succès!\n📋 ${result.detachedRdv} rendez-vous ont été détachés du formulaire et conservés.`);
      } else {
        notifySuccess("✅ Formulaire supprimé avec succès!");
      }
      
    } catch (error) {
      console.error("Erreur réseau lors de la suppression:", error);
      notifyError("❌ Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsDeleting(false); // Arrêter le chargement dans tous les cas
    }
  };

  const modifyForm = async (form) => {
    try {
      const response = await fetch(`/forms/${form.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch form details");
      }

      const fullFormData = await response.json();
      console.log("Full form data:", fullFormData);

      // Set the form with complete field data
      setSelectedForm(fullFormData);
      setShowModifyPopup(form.id);
    } catch (error) {
      console.error("Error fetching form details:", error);
    }
  };

  // Fonction pour mettre à jour le formulaire sélectionné après modification
  const updateSelectedForm = async (formId) => {
    try {
      const response = await fetch(`/forms/${formId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (response.ok) {
        const updatedFormData = await response.json();
        setSelectedForm(updatedFormData);
      }
    } catch (error) {
      console.warn("Impossible de mettre à jour l'affichage du formulaire:", error);
    }
  };

  const fetchForms = async (forceRefresh = false) => {
    try {
      console.log("Fetching forms...", forceRefresh ? "(force refresh)" : "");
      const response = await fetch("/forms/get-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Ajouter cache-control pour forcer le rafraîchissement
          ...(forceRefresh && { "Cache-Control": "no-cache, no-store, must-revalidate" })
        },
        body: JSON.stringify({
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`Failed to fetch forms: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Fetched forms data:", data);
      
      // Ensure we're setting an array to state
      setForms(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching forms:", error);
      setForms([]); // Set to empty array on error
    }
  };

  useEffect(() => {
    if (showPopup) {
      gsap.to(".general-data-form-button", {
        rotate: 45,
        duration: 0.25,
        ease: "power1.inOut",
      });
    } else {
      gsap.to(".general-data-form-button", {
        rotate: 0,
        duration: 0.25,
        ease: "power1.inOut",
      });
    }
  }, [showPopup]);

  useEffect(() => {
    fetchForms();
  }, []);

  return (
    <div className="create-form-container creation-container">
      <div className="create-form-header creation-container-header">
        <div className="create-form-title-container title-container">
          <h1 className="create-form-title">Formulaires</h1>
          <div className="create-form-title-underline"></div>
        </div>
        <button
          onClick={() => setShowPopup(!showPopup)}
          className={`general-data-form-button ${showPopup ? "close" : "open"}`}
        >
          +
        </button>
      </div>
      {forms.length === 0 ? (
        <div className="no-forms-message">Aucun formulaire disponible</div>
      ) : (
        <div className="info-card-grid">
          {forms.map((form) => (
            <div key={form.id} className="info-card">
              <p className="info-card-name">{form.name}</p>
              <div className="info-card-name-underline"></div>
              <p className="info-card-label">Description</p>
              <p className="info-card-value">
                {form.description || "Aucune description"}
              </p>
              <p className="info-card-label">Questions</p>
              <p className="info-card-value">
                {Array.isArray(form.fields) ? form.fields.length : 0}{" "}
                question(s)
              </p>
              <div className="info-card-buttons row">
                <button onClick={() => modifyForm(form)}>Modifier</button>
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(true);
                    setSelectedForm(form);
                  }}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showPopup && (
        <div
          className="create-form-popup-container"
          onClick={() => setShowPopup(false)}
        >
          <FormCreation fetchForms={fetchForms} setShowPopup={setShowPopup} />
        </div>
      )}
      {showModifyPopup && selectedForm && (
        <div
          className="create-form-popup-container"
          onClick={() => setShowModifyPopup(null)}
        >
          <ModifyForm
            fetchForms={fetchForms}
            setShowModifyPopup={setShowModifyPopup}
            form={selectedForm}
            updateSelectedForm={updateSelectedForm}
          />
        </div>
      )}
      {showDeleteConfirmation && selectedForm && (
        <div
          className="create-form-popup-container"
          onClick={() => {
            setShowDeleteConfirmation(false);
            setSelectedForm(null);
          }}
        >
          <DeleteConfirmationPopup
            onConfirm={() => deleteForm(selectedForm.id)}
            onCancel={() => {
              setShowDeleteConfirmation(false);
              setSelectedForm(null);
            }}
            isDeleting={isDeleting}
          />
        </div>
      )}
    </div>
  );
}

export default CreateForm;
